import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError } from "axios";
import { useToastStore } from "../app/store/useToastStore";
import { useErrorReportStore } from "../app/store/useErrorReportStore";
import { getEnhancedApiErrorMessage } from "../shared/utils/errorUtils";
import { trackException } from "../shared/utils/telemetry";

// Define service types
export const ServiceType = {
  SENDER: "sender",
  SENDER2: "sender2",
  SENDER3: "sender3",
  traveller: "traveller",
  traveller2: "traveller2",
  traveller3: "traveller3",
  USER: "user",
  USER2: "user2",
  USER3: "user3",
  NOTIFICATION : "notification",
  NOTIFICATION2 : "notification2",
  NOTIFICATION3 : "notification3",
  PAYOUT: "payout"
} as const;

export type ServiceType = (typeof ServiceType)[keyof typeof ServiceType];

// Map environment variables to service types
const SERVICE_BASE_URLS = {
  [ServiceType.SENDER]: import.meta.env.VITE_APP_API_BASE_URL_SENDER,
  [ServiceType.SENDER2]: import.meta.env.VITE_APP_API_BASE_URL_SENDER_V2,
  [ServiceType.SENDER3]: import.meta.env.VITE_APP_API_BASE_URL_SENDER_V3,
  [ServiceType.traveller]: import.meta.env.VITE_APP_API_BASE_URL_traveller,
  [ServiceType.traveller2]: import.meta.env.VITE_APP_API_BASE_URL_traveller_V2,
  [ServiceType.traveller3]: import.meta.env.VITE_APP_API_BASE_URL_traveller_V3,
  [ServiceType.USER]: import.meta.env.VITE_APP_API_BASE_URL_USER,
  [ServiceType.USER2]: import.meta.env.VITE_APP_API_BASE_URL_USER_V2,
  [ServiceType.USER3]: import.meta.env.VITE_APP_API_BASE_URL_USER_V3,
  [ServiceType.NOTIFICATION]: import.meta.env.VITE_APP_API_BASE_URL_NOTIFICATION,
  [ServiceType.NOTIFICATION2]: import.meta.env.VITE_APP_API_BASE_URL_NOTIFICATION_V2,
  [ServiceType.NOTIFICATION3]: import.meta.env.VITE_APP_API_BASE_URL_NOTIFICATION_V3,
  [ServiceType.PAYOUT]: import.meta.env.VITE_APP_API_BASE_URL_PAYOUT
};

// Create and cache axios instances for each service
const axiosInstances: Record<ServiceType, AxiosInstance> = {} as Record<
  ServiceType,
  AxiosInstance
>;

let authToken: string | null = null;

// Track last error timestamp to prevent toast spam
let lastNetworkErrorTime = 0;
const ERROR_THROTTLE_MS = 10000; // Only show same error type once every 10 seconds

// Function to get or create an axios instance for a specific service
export const getApiService = (serviceType: ServiceType): AxiosInstance => {
  // Return cached instance if available
  if (axiosInstances[serviceType]) {
    return axiosInstances[serviceType];
  }

  const baseURL = SERVICE_BASE_URLS[serviceType];

  if (!baseURL) {
    throw new Error(`API service ${serviceType} not configured properly`);
  }

  // Create new axios instance
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Block request immediately if offline
      if (!navigator.onLine) {
        const now = Date.now();
        if (now - lastNetworkErrorTime > ERROR_THROTTLE_MS) {
          useToastStore.getState().showToast("No internet connection", "error", 5000);
          lastNetworkErrorTime = now;
        }
        return Promise.reject(new Error("No internet connection"));
      }

      // Check if this is a FormData request
      const isFormData = config.data instanceof FormData;

      if (isFormData) {
        // For FormData requests, let the browser set the content type with boundary
        delete config.headers["Content-Type"];
      }


      // Don't clobber an Authorization header the request already set explicitly
      // (e.g. the receiver guest token used for group chat requests) with the
      // shared Auth0 token.
      if (!authToken || config.headers.Authorization) {
        return config;
      }

      // Set Authorization header with Bearer token
      config.headers.Authorization = `Bearer ${authToken}`;

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor with enhanced error handling
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: AxiosError) => {
      const now = Date.now();
      const showToast = useToastStore.getState().showToast;
      const setCorrelationId = useErrorReportStore.getState().setCorrelationId;

      // Get enhanced error response with correlation ID support
      const errorResponse = getEnhancedApiErrorMessage(error);
      const statusCode = error.response?.status;

      // Handle network errors
      if (!error.response) {
        if (now - lastNetworkErrorTime > ERROR_THROTTLE_MS) {
          showToast("No internet connection available", "error", 5000);
          lastNetworkErrorTime = now;
          trackException(error as Error, "API_NETWORK", {
            url: error.config?.url,
          });
        }
        return Promise.reject(error);
      }

      // Handle 401 errors - silently fire token refresh event, never show toast
      if (statusCode === 401) {
        window.dispatchEvent(new CustomEvent('token:refresh-needed'));
        return Promise.reject(error);
      }

      // Handle 404 errors - don't show toast (often expected)
      if (statusCode === 404) {
        return Promise.reject(error);
      }

      // Handle 5xx server errors with blocking toast and report action
      if (errorResponse.isServerError && statusCode && statusCode >= 500) {
        if (now - lastNetworkErrorTime > ERROR_THROTTLE_MS) {
          // Store correlation ID for report issue flow
          if (errorResponse.correlationId) {
            setCorrelationId(errorResponse.correlationId);
          }

          // Show blocking toast with report action
          showToast(
            errorResponse.message,
            "error",
            0, // No auto-dismiss for blocking toasts
            {
              blocking: true,
              action: {
                label: "Report Issue",
                onClick: () => {
                  // Dispatch a custom event to navigate using React Router
                  // This avoids full page reload and React Hooks order issues
                  const navigationEvent = new CustomEvent('navigate-to-report-issue');
                  window.dispatchEvent(navigationEvent);
                },
              },
            }
          );
          lastNetworkErrorTime = now;

          // Send to Azure telemetry with the backend correlation ID attached
          trackException(error as Error, "API_SERVER", {
            url: error.config?.url,
            status: statusCode,
            correlationId: errorResponse.correlationId,
          });
        }
        return Promise.reject(error);
      }

      // Handle 4xx client errors - show backend message directly
      if (statusCode && statusCode >= 400 && statusCode < 500) {
        // Don't throttle 4xx errors - they are usually specific to user actions
        showToast(errorResponse.message, "error", 5000);
        trackException(error as Error, "API_CLIENT", {
          url: error.config?.url,
          status: statusCode,
          correlationId: errorResponse.correlationId,
        });
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  // Store in cache
  axiosInstances[serviceType] = instance;
  return instance;
};

// Helper to set auth token for all service instances
export const setAuthTokenForAllServices = (token: string | null) => {
  if (!token) {
    authToken = null;
    return;
  }

  authToken = token;
};

// Export default API service (backward compatibility)
export default getApiService(ServiceType.USER);